import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LBtn, LInputField } from "@components";
import { searchExamineesFilter } from "@fadmin/tabs/results/components/filterHelper";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getDetails } from "@helpers/examineeHelper";
import { useApi } from "@services/useApi";
import { ExistingExamineeDetails } from "@store/generalTypes";
import { ExistingExaminee, updateLocationExaminees } from "@store/locationsSlice";
import { Colors } from "@styles/colors";
import classNames from "classnames";
import { format } from "date-fns";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
    ExamineeContactInfo,
    resetExaminee,
    setExamineeId,
    updateContactInfo,
    updateExamineeAssignedTo
} from "../../store/newExamineeSlice";
import { ExamineeCard } from "../ExamineeCard";

const ExamineeBox = ({ examinee, isFirst, isLast, onSelected }: ExamineeBoxProps): JSX.Element => {
    const containerClasses = classNames(
        "is-flex",
        "columns-margins",
        "has-background-lightest-blue",
        isFirst ? "is-bordered-top" : "",
        isLast ? "is-bordered-bottom" : "",
        "has-text-ldark-gray"
    );

    return (
        <a onClick={onSelected} className={containerClasses} style={{ border: "2px #B6CBD6 solid" }}>
            <div className="column p-4 mx-4">
                <p className="has-text-weight-bold is-size-5">
                    {examinee.firstName} {examinee.lastName}
                </p>
                <div className="columns mb-0">
                    <div className="column is-two-thirds">
                        <p className="is-size-6">{examinee.email ?? "Not specified"}</p>
                        <p className="is-size-6">{examinee.phoneNumber}</p>
                    </div>
                    {!!examinee.lastAssessment && (
                        <div className="column">
                            <p className="is-size-6 has-text-weight-bold">Last Test</p>
                            <p className="is-size-6">{format(new Date(examinee.lastAssessment), "PP")}</p>
                        </div>
                    )}
                </div>
            </div>
        </a>
    );
};

interface ExamineeBoxProps {
    isFirst?: boolean;
    isLast?: boolean;
    examinee: ExistingExaminee;
    onSelected?: () => void;
}

const mapExistingToContactInfo = (examinee: ExistingExaminee | ExistingExamineeDetails): ExamineeContactInfo => {
    return {
        id: examinee.id,
        firstName: examinee.firstName,
        lastName: examinee.lastName,
        phoneNumber: examinee.phoneNumber,
        email: examinee.email
    };
};

export const ExistingExaminees = (): JSX.Element => {
    const validateMethods = useForm();

    const dispatch = useAppDispatch();

    const [searchValue, setSearchValue] = useState("");
    const [selectedExaminee, setSelectedExaminee] = useState<ExistingExaminee>();
    const [selectedExamineeDetails, setSelectedExamineeDetails] = useState<ExistingExamineeDetails>();

    const { getLocationExaminees, loading, getExamineeDetails } = useApi();

    const locationsExaminees = useAppSelector((x) => x.orgLocations.existingExaminees);
    const currentLocation = useAppSelector((x) => x.orgLocations.currentLocation);

    const handleGetExamineeDetails = async (): Promise<void> => {
        const examinee = await getExamineeDetails(currentLocation!.id, Number(selectedExaminee!.id));
        setSelectedExamineeDetails(examinee);
        dispatch(setExamineeId({ id: examinee.id.toString(), displayId: "" }));
        dispatch(updateExamineeAssignedTo({ location: currentLocation!.id, clinicianId: examinee.clinicianId }));
        dispatch(updateContactInfo(mapExistingToContactInfo(examinee)));
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        setSearchValue(e.target.value);
    };

    const handleSelectExaminee = (examinee: ExistingExaminee): void => {
        setSelectedExaminee(examinee);
    };

    const handleGoBack = (): void => {
        setSelectedExaminee(undefined);
        setSelectedExamineeDetails(undefined);
        dispatch(resetExaminee());
    };

    const filteredUsers = searchExamineesFilter(searchValue, locationsExaminees);

    useEffect(() => {
        if (selectedExaminee) {
            void handleGetExamineeDetails();
        }
    }, [selectedExaminee]);

    const handleGetExaminees = useCallback(async () => {
        const examinees = await getLocationExaminees(currentLocation!.id);
        dispatch(updateLocationExaminees(examinees.items));
    }, [currentLocation]);

    useEffect(() => {
        void handleGetExaminees();
    }, []);

    return (
        <div className="container">
            <div className="mw-60">
                {!selectedExaminee ? (
                    <FormProvider {...validateMethods}>
                        <LInputField
                            label="Search Existing Examinees"
                            inputName="search"
                            isSearch={true}
                            placeholder="Enter name, email address, or phone number"
                            onChange={handleSearch}
                            isExpanded
                        />
                        {!loading &&
                            filteredUsers.map((x, idx) => {
                                return (
                                    <div key={`${x.id}-${idx}`}>
                                        <ExamineeBox
                                            examinee={x}
                                            isFirst={idx === 0}
                                            isLast={idx === filteredUsers.length - 1}
                                            onSelected={() => handleSelectExaminee(x)}
                                        />
                                    </div>
                                );
                            })}
                        {loading && (
                            <div className="is-flex is-justify-content-center mt-4">
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    size="4x"
                                    color={Colors.darkBlue}
                                    className="fa-spin"
                                />
                            </div>
                        )}
                        {!loading && !filteredUsers.length ? <p>No users match this value</p> : <></>}
                    </FormProvider>
                ) : (
                    <div>
                        <LBtn onClick={handleGoBack} variation="unstyled" noSpacing isBold>
                            {"< Back"}
                        </LBtn>
                        <ExamineeCard
                            contactInfo={mapExistingToContactInfo(selectedExaminee)}
                            bgColor="#F0F7FA"
                            loading={loading}
                            classList="p-6 mx-5"
                            examineeDetails={selectedExamineeDetails && getDetails(selectedExamineeDetails)}
                            clinician={
                                selectedExamineeDetails &&
                                `${selectedExamineeDetails?.clinicianFirstName} ${selectedExamineeDetails?.clinicianLastName}`
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
